import {
  completeTaskForToday,
  CompleteTaskForTodayResultKind,
} from '@griply/utils';
import { useCallback } from 'react';
import { useOpenManualEntryLogging } from 'shared/contexts/habit-manual-entry-logging';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalTaskCountMutation } from 'shared/hooks/queries/use-update-goal-task-count-mutation';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { Task, UpdateTask } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';

import { useCompleteTask } from './use-complete-task';
import { useUpdateHabitScheduleEntry } from './use-update-habit-schedule-entry';
import { useUpdateTask } from './use-update-task';

export const useTaskTodayControls = () => {
  const today = useToday();
  const { submit: updateTask } = useUpdateTask();
  const { mutate: updateGoalTaskCount } = useUpdateGoalTaskCountMutation();
  const completeTask = useCompleteTask();
  const updateHabitEntry = useUpdateHabitScheduleEntry();

  const openTaskDetail = useOpenTaskDetail();
  const openManualEntryLogging = useOpenManualEntryLogging();

  const onComplete = useCallback(
    (
      task: Task,
      type = ScheduleEntryState.Complete,
      options?: {
        value?: number;
        increment?: number;
        referenceDate?: Timestamp;
        toggleDayEntry?: boolean;
        keepDetailOpen?: boolean;
      },
    ) => {
      const result = completeTaskForToday(task, {
        today,
        type,
        referenceDate: options?.referenceDate,
        value: options?.value,
        increment: options?.increment,
        toggleDayEntry: options?.toggleDayEntry,
      });

      if (result.kind === CompleteTaskForTodayResultKind.ManualEntryRequired) {
        openManualEntryLogging(task.id, options?.referenceDate ?? today);
        (!options?.keepDetailOpen || !task.parentIds?.length) &&
          openTaskDetail(undefined);
        return;
      }

      if (result.kind === CompleteTaskForTodayResultKind.Ignored) {
        return;
      }

      if (task.type === TaskType.Task) {
        completeTask(task, options?.referenceDate);
        (!options?.keepDetailOpen || !task.parentIds?.length) &&
          openTaskDetail(undefined);
        return;
      }

      updateHabitEntry(task, options?.referenceDate ?? new Date(), type, {
        ...options,
        toggle: options?.toggleDayEntry,
      });
    },
    [
      completeTask,
      openManualEntryLogging,
      openTaskDetail,
      today,
      updateHabitEntry,
    ],
  );

  const onUpdate = useCallback(
    (initialTask: Task, updatedTask: UpdateTask) => {
      if (updatedTask.goalId !== initialTask.goalId) {
        // update the goals with the correct counts
        updatedTask.goalId &&
          updateGoalTaskCount({
            goalId: updatedTask.goalId,
            taskCount: 1,
            completedTaskCount:
              updatedTask.completedAt || initialTask.completedAt ? 1 : 0,
          });

        initialTask.goalId &&
          updateGoalTaskCount({
            goalId: initialTask.goalId,
            taskCount: -1,
            completedTaskCount: initialTask.completedAt ? -1 : 0,
          });
      }
      updateTask(updatedTask);
    },
    [updateGoalTaskCount, updateTask],
  );

  return {
    onUpdate,
    onComplete,
    openManualEntryLogging,
  };
};
